/**
 * 承認一覧（`/approvals`）。未確定の全案件の承認事項を 1 表に並べ、担当者が判断する。
 *
 * **案件を跨ぐ別画面にした。** 承認する担当者が見るのは「この案件の提案は通してよいか」では
 * なく「同じ語や分類を別の案件が別の意味で提案していないか」「用語や分類の追加が 1 つの案件に偏っていないか」
 * である。案件サマリーの節に置いていたときは案件を 1 つずつ開くしかなく、100 件近い提案を
 * 抱えた案件の判断が実務にならなかった。
 *
 * **読むのは要素表（`spec_graph_*`）である。** 一覧は `listTermChangesAcrossPjs` が
 * 未確定の案件の承認の対象を 1 回で読み、承認は `approveTermChanges` が案件のノードへ
 * 積む。指す相手が変更行の id からノードの id へ替わった（決定 7）ので、選択の鍵も
 * 「案件 : ノード」である（`approvalRowKey`）。
 *
 * **絞り込み（種別・状態・操作の切替と案件）は画面の中の状態に持つ。** URL には出さない —
 * この画面は担当者が判断のために開くもので、絞った状態を他人へ渡す対象ではない
 * （`PjsPage` の絞り込みと同じ）。種別は契約の承認の種別の全部で、
 * 提案が 0 件でも常に出す。絞り直すと選択は外す — 見えなくなった行を選んだまま送らない。
 *
 * **判断は複数の行にまとめて積む。** 承認の書込は案件単位なので、選んだ行を案件ごとに畳んで
 * 案件の数だけ送る（`groupSelectionByPj`）。1 回に積める上限を超える選択は送る前に止める。
 * 却下は理由を必須にする — 却下が提案者へ「なぜ通らないか」を返す唯一の経路だからである。
 *
 * **担当者でない人には画面を出さない。** 権限は API の照合（`getApprovalAccess`）が持ち、
 * 画面はその結果を写すだけである。照合を待たずに一覧を引くと、担当者でない人の呼び出しが
 * `FORBIDDEN` で拒否され、拒否のログが利用者の数だけ残る。
 *
 * **見せるのは対象 ・ 操作の札 ・ 選択の箱と「承認する」だけにする**（Dhik 2026-09-29「本当に出すべき情報を
 * 1 or 2 個に」）。件数は見出しの `title`、行の案件 ・ 種別 ・ 状態 ・ 提案者と日時は行の `title` に載せ、
 * 状態と操作の絞り込み・却下の理由の欄は畳む。条件に合う行が無いときの案内の文は置かない。
 * 表示部品（絞り込み / 行 / 表 / 一括操作）と結末の畳み込みは `./approvals/` に分けている。
 * この画面が持つのは、取得の状態に応じた描き分けと、絞り込み・選択・送信の状態だけである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 未確定の全案件の承認事項を 1 表に並べ、4 軸で絞り込み、選んだ行にまとめて承認 / 却下を積む担当者の画面。
 * @graph-connects useApprovalAccess [calls] 本人が承認を積める担当者かの照合
 * @graph-connects useTermChangesAcrossPjs [calls] 未確定の全案件の承認対象の取得
 * @graph-connects useApproveTermChanges [calls] 承認 / 却下の書込
 */
import type { ApprovalDecision } from '@cortex/domain-spec-console';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState, ErrorState } from '../components/spec/fallback-states.js';
import {
  useApproveTermChanges,
  useTermChangesAcrossPjs,
} from '../hooks/queries/spec-pj-element-queries.js';
import { useApprovalAccess } from '../hooks/queries/spec-pj-queries.js';
import { ApprovalActions } from './approvals/approval-actions.js';
import { ApprovalFilterBar } from './approvals/approval-filter-bar.js';
import { ApprovalTable } from './approvals/approval-table.js';
import { summarizeOutcomes, type BatchOutcome } from './approvals/batch-outcome.js';
import { isSelectableApproval } from './approvals/approval-row.js';
import {
  approvalRowKey,
  DEFAULT_APPROVAL_FILTER,
  exceedsBatchLimit,
  filterApprovalItems,
  groupSelectionByPj,
  pjOptionsOf,
  type ApprovalFilter,
} from './approvals-selection.js';

/**
 * 承認一覧の本体。照合 → 一覧 → 表の順に、取得中・失敗・拒否・空・本体で描き分ける。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認一覧を、担当者かの照合と一覧の取得の状態に応じて描き分け、絞り込み・選択・一括判断を束ねる。
 * @graph-connects useApprovalAccess [calls] 本人が承認を積める担当者かの照合
 * @graph-connects useTermChangesAcrossPjs [calls] 未確定の全案件の承認対象の取得
 * @graph-connects useApproveTermChanges [calls] 承認 / 却下の書込
 * @graph-connects ApprovalFilterBar [calls] 絞り込み 4 軸
 * @graph-connects ApprovalTable [calls] 承認事項の表
 * @graph-connects ApprovalActions [calls] 一括の承認 / 却下
 */
export function ApprovalsPage() {
  const { t } = useTranslation();
  const access = useApprovalAccess();
  const canApprove = access.data?.canApprove === true;
  const list = useTermChangesAcrossPjs(canApprove);
  const approval = useApproveTermChanges();
  const [filter, setFilter] = useState<ApprovalFilter>(DEFAULT_APPROVAL_FILTER);
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<BatchOutcome | null>(null);

  const items = list.data?.approvals ?? [];
  const shown = filterApprovalItems(items, filter);
  const batches = groupSelectionByPj(shown, selected);
  const selectedCount = batches.reduce((total, batch) => total + batch.nodeIds.length, 0);

  /* 件数は見出しの title に載せる。一覧を読み終えるまで title を付けない（0 件と読めてしまう）。 */
  const header = (
    <header className="flex flex-wrap items-center gap-3">
      <h1
        data-approval-shown={list.data === undefined ? undefined : shown.length}
        data-approval-total={list.data === undefined ? undefined : items.length}
        title={list.data === undefined ? undefined : t('approvals.count', { count: shown.length })}
        className="text-2xl font-bold text-[var(--color-text-primary)]"
      >
        {t('approvals.title')}
      </h1>
    </header>
  );

  if (access.error !== null && access.error !== undefined) {
    return <ErrorState error={access.error} />;
  }
  if (access.isLoading || access.data === undefined) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="4rem" />
        <Skeleton variant="rectangular" height="12rem" />
      </div>
    );
  }
  if (!canApprove) {
    return (
      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
        {header}
        <EmptyState message={t('approvals.forbidden')} />
      </div>
    );
  }
  if (list.error !== null && list.error !== undefined) {
    return <ErrorState error={list.error} />;
  }
  if (list.isLoading || list.data === undefined) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('common.loading')}>
        <Skeleton variant="rectangular" height="4rem" />
        <Skeleton variant="rectangular" height="12rem" />
      </div>
    );
  }

  const decide = (decision: ApprovalDecision, decisionReason: string | null) => {
    setSending(true);
    setOutcome(null);
    Promise.all(
      batches.map((batch) =>
        approval.mutateAsync({
          pjNumber: batch.pjNumber,
          nodeIds: [...batch.nodeIds],
          decision,
          reason: decisionReason,
        }),
      ),
    )
      .then((results) => {
        setOutcome(summarizeOutcomes(results));
        setSelected(new Set<string>());
        setReason('');
      })
      .catch((error: unknown) => {
        // 送信の失敗は画面に出す。一覧は成功したぶんだけ取り直されている。
        setOutcome({
          kind: 'failed',
          message: error instanceof Error ? error.message : String(error),
        });
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      {header}

      <Card padding="md">
        <ApprovalFilterBar
          filter={filter}
          pjOptions={pjOptionsOf(items)}
          onChange={(next) => {
            setFilter(next);
            setSelected(new Set<string>());
          }}
        />
      </Card>

      <section aria-label={t('approvals.title')}>
        <Card padding="md">
          {shown.length > 0 && (
            <ApprovalTable
              items={shown}
              selected={selected}
              onToggle={(rowKey, isSelected) => {
                setSelected((current) => {
                  const next = new Set(current);
                  if (isSelected) next.add(rowKey);
                  else next.delete(rowKey);
                  return next;
                });
              }}
              onToggleAll={(isSelected) => {
                setSelected((current) => {
                  const next = new Set(current);
                  for (const item of shown.filter(isSelectableApproval)) {
                    if (isSelected) next.add(approvalRowKey(item));
                    else next.delete(approvalRowKey(item));
                  }
                  return next;
                });
              }}
            />
          )}
          {items.length > 0 && (
            <div className="sticky bottom-0 mt-3 bg-[var(--color-bg-base)]">
              <ApprovalActions
                selectedCount={selectedCount}
                overLimit={exceedsBatchLimit(batches)}
                sending={sending || approval.isPending}
                outcome={outcome}
                reason={reason}
                onReasonChange={setReason}
                onDecide={decide}
              />
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}
